import { PayloadAction } from "@reduxjs/toolkit";
import { get, STORAGE_KEYS } from "API/LocalStorage";
import { getQueryParameter } from "API/Location";
import { ThemeType } from "Model/Configuration/ITheme";
import { IProject } from "Model/Storage/IProject";
import {
  all, call, put, select, spawn, takeEvery
} from "redux-saga/effects";
import {
  projectsAdd,
  projectsClear,
  projectsFilterKeySet,
  projectsFilterLoad,
  projectsRetrieveAutomatic,
  projectsRetrieveStart,
  uiAppInit,
  uiRefreshEveryTimeLoad,
  uiSetActiveProjectId,
  uiSetDetailsOpen,
  uiSetTheme,
  uiUsernamesLoad,
  versionsGetStart
} from "Store/Actions";
import { selectProjectTotalCount } from "Store/Selectors/Projects";
import { Configuration } from "Utils/Configuration";
import { isNotNullOrWhiteSpace } from "Utils/String";
import { getProjectsById } from "Workers/Projects";
import { callWithAuth } from "./CallWithAuth";
import { sortAndFilterSaga } from "./Projects";

function* appInitSaga({ payload: forceReload }: PayloadAction<boolean>) {
  const config = yield call(Configuration.getInstanceAsync);
  const totalCount: number = yield select(selectProjectTotalCount);

  let themeType = get<ThemeType>(STORAGE_KEYS.THEME_CONFIG);
  if (themeType == null) {
    themeType = config.getDefaultThemeType();
  }
  yield put(uiSetTheme(themeType));

  if (totalCount <= 0 || forceReload) {
    yield put(projectsFilterKeySet(config.getInitialFilterCategory()));
  }

  yield all([put(projectsFilterLoad()), put(versionsGetStart())]);

  yield spawn(initByProjectId);

  if (totalCount <= 0 || forceReload) {
    yield all([put(projectsClear()), put(projectsRetrieveStart())]);
  }

  yield all([put(uiUsernamesLoad()), put(uiRefreshEveryTimeLoad()), put(projectsRetrieveAutomatic())]);
}

function* initByProjectId() {
  const pid = yield call(getQueryParameter, `pid`);
  if (isNotNullOrWhiteSpace(pid)) {
    const projects: IProject[] = yield callWithAuth(getProjectsById, [pid]);
    yield put(projectsAdd(projects));
    yield all([
      spawn(sortAndFilterSaga, { filter: false }),
      put(uiSetActiveProjectId(pid)),
      put(uiSetDetailsOpen(true))
    ]);
  }
}

export function* AppSaga(): unknown {
  yield takeEvery(uiAppInit, appInitSaga);
}
